/**
 * ActividadForm — shared component para alta + edit con discriminated sub-forms.
 *
 * Per FWA-PWA-030 G0 D3: RHF + zodResolver con ActividadFullCreatePayloadSchema
 * (discriminated union por tipo). Sub-form condicional para servicio /
 * reclutamiento / evento_internacional.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import {
  type ActividadFullCreatePayload,
  ActividadFullCreatePayloadSchema,
  type ActividadUpdatePayload,
  ActividadUpdatePayloadSchema,
} from '@sisrep/schemas';
import { useForm } from 'react-hook-form';
import { Button } from './ui/Button.js';
import { Input } from './ui/Input.js';

const TIPO_OPTIONS = [
  { value: 'junta_semanal', label: 'Junta semanal' },
  { value: 'evento', label: 'Evento' },
  { value: 'servicio', label: 'Servicio' },
  { value: 'reclutamiento', label: 'Reclutamiento' },
  { value: 'capacitacion', label: 'Capacitación' },
  { value: 'evento_internacional', label: 'Evento internacional' },
] as const;

const RAMA_OPTIONS = ['manada', 'tropa', 'caravana', 'comunidad'] as const;

interface ActividadFormCreateProps {
  mode: 'create';
  initialValues?: Partial<ActividadFullCreatePayload>;
  onSubmit: (payload: ActividadFullCreatePayload) => void;
  isSubmitting?: boolean;
  serverError?: string;
}

interface ActividadFormEditProps {
  mode: 'edit';
  initialValues: Partial<ActividadUpdatePayload> & { tipo: string };
  onSubmit: (payload: ActividadUpdatePayload) => void;
  isSubmitting?: boolean;
  serverError?: string;
}

type ActividadFormProps = ActividadFormCreateProps | ActividadFormEditProps;

export function ActividadForm(props: ActividadFormProps) {
  if (props.mode === 'create') {
    return <ActividadFormCreate {...props} />;
  }
  return <ActividadFormEdit {...props} />;
}

// ─── Create variant (discriminated by tipo) ───────────────────────────────

function splitLines(value: string | undefined | null): string[] | null {
  if (!value || !value.trim()) return null;
  return value
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
}

function joinLines(arr: string[] | null | undefined): string {
  return arr ? arr.join('\n') : '';
}

function ActividadFormCreate({
  initialValues,
  onSubmit,
  isSubmitting,
  serverError,
}: ActividadFormCreateProps) {
  // biome-ignore lint/suspicious/noExplicitAny: RHF + zodResolver + exactOptionalPropertyTypes
  const resolver = zodResolver(ActividadFullCreatePayloadSchema) as any;
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    // biome-ignore lint/suspicious/noExplicitAny: see above
  } = useForm<any>({
    resolver,
    mode: 'onBlur',
    defaultValues: { tipo: 'junta_semanal', rama: 'manada', ...initialValues },
  });
  const tipo = watch('tipo') as string;

  // biome-ignore lint/suspicious/noExplicitAny: form payload pre-zod
  const handleFormSubmit = (raw: any) => {
    // Transform textarea strings → arrays for multi-input fields
    const transformed = {
      ...raw,
      evidencias_urls: splitLines(raw.evidencias_urls_text),
      fotos: splitLines(raw.fotos_text),
    };
    if (tipo === 'servicio' && transformed.subtype_servicio) {
      transformed.subtype_servicio = {
        ...transformed.subtype_servicio,
        aliados: splitLines(transformed.subtype_servicio.aliados_text),
      };
    }
    onSubmit(transformed as ActividadFullCreatePayload);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      {serverError && (
        <div
          className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700"
          role="alert"
        >
          {serverError}
        </div>
      )}

      <Input
        label="Nombre de la actividad"
        {...register('nombre')}
        error={errors.nombre?.message as string | undefined}
      />
      <Input
        label="Lugar"
        {...register('lugar')}
        error={errors.lugar?.message as string | undefined}
      />
      <Input
        label="Fecha de la actividad"
        type="date"
        {...register('fecha_actividad')}
        error={errors.fecha_actividad?.message as string | undefined}
      />
      <Input
        label="Fecha de registro"
        type="date"
        {...register('fecha_registro')}
        error={errors.fecha_registro?.message as string | undefined}
      />

      <label className="flex flex-col gap-1">
        <span className="text-sm font-medium text-gray-700">Tipo</span>
        <select
          className="px-3 py-2 border border-gray-300 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-distrito-500"
          {...register('tipo')}
        >
          {TIPO_OPTIONS.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-sm font-medium text-gray-700">Rama</span>
        <select
          className="px-3 py-2 border border-gray-300 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-distrito-500"
          {...register('rama')}
        >
          {RAMA_OPTIONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </label>

      <Input
        label="Participantes"
        type="number"
        {...register('participantes', { valueAsNumber: true })}
        error={errors.participantes?.message as string | undefined}
      />
      <Input
        label="Beneficiarios (opcional)"
        type="number"
        {...register('beneficiarios', { valueAsNumber: true })}
        error={errors.beneficiarios?.message as string | undefined}
      />
      <Input
        label="Horas"
        type="number"
        step="0.5"
        {...register('horas', { valueAsNumber: true })}
        error={errors.horas?.message as string | undefined}
      />

      <label className="flex flex-col gap-1">
        <span className="text-sm font-medium text-gray-700">Evidencias (URLs, una por línea)</span>
        <p className="text-xs text-amber-700">
          No incluyas nombres de niñas en los nombres de archivo.
        </p>
        <textarea
          className="px-3 py-2 border border-gray-300 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-distrito-500"
          rows={3}
          {...register('evidencias_urls_text')}
          defaultValue={joinLines(initialValues?.evidencias_urls)}
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-sm font-medium text-gray-700">
          Fotos (URLs, máx 5, una por línea)
        </span>
        <p className="text-xs text-amber-700">
          No incluyas nombres de niñas en los nombres de archivo.
        </p>
        <textarea
          className="px-3 py-2 border border-gray-300 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-distrito-500"
          rows={3}
          {...register('fotos_text')}
          defaultValue={joinLines(initialValues?.fotos)}
        />
      </label>

      {/* ─── Sub-form: servicio ─────────────────────────────────────────── */}
      {tipo === 'servicio' && (
        <fieldset className="border border-gray-200 rounded-md p-3 space-y-3">
          <legend className="text-sm font-semibold text-gray-700 px-2">Datos de servicio</legend>
          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium text-gray-700">Tipo de servicio</span>
            <select
              className="px-3 py-2 border border-gray-300 rounded-md"
              {...register('subtype_servicio.tipo_servicio')}
            >
              <option value="comunidad">Comunidad</option>
              <option value="ambiental">Ambiental</option>
              <option value="social">Social</option>
              <option value="educativo">Educativo</option>
              <option value="otro">Otro</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium text-gray-700">Descripción del servicio</span>
            <textarea
              className="px-3 py-2 border border-gray-300 rounded-md"
              rows={3}
              {...register('subtype_servicio.descripcion_servicio')}
            />
          </label>
          <Input
            label="Comunidad beneficiada (opcional)"
            {...register('subtype_servicio.comunidad_beneficiada')}
          />
          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium text-gray-700">Aliados (uno por línea)</span>
            <textarea
              className="px-3 py-2 border border-gray-300 rounded-md"
              rows={2}
              {...register('subtype_servicio.aliados_text')}
            />
          </label>
        </fieldset>
      )}

      {/* ─── Sub-form: reclutamiento ────────────────────────────────────── */}
      {tipo === 'reclutamiento' && (
        <fieldset className="border border-gray-200 rounded-md p-3 space-y-3">
          <legend className="text-sm font-semibold text-gray-700 px-2">
            Datos de reclutamiento
          </legend>
          <Input
            label="Lugar de reclutamiento"
            {...register('subtype_reclutamiento.lugar_reclutamiento')}
          />
          <Input
            label="Prospectos contactados"
            type="number"
            {...register('subtype_reclutamiento.prospectos_contactados', { valueAsNumber: true })}
          />
          <Input
            label="Prospectos interesados"
            type="number"
            {...register('subtype_reclutamiento.prospectos_interesados', { valueAsNumber: true })}
          />
          <Input
            label="Prospectos inscritos"
            type="number"
            {...register('subtype_reclutamiento.prospectos_inscritos', { valueAsNumber: true })}
          />
          <Input
            label="Material distribuido (opcional)"
            {...register('subtype_reclutamiento.material_distribuido')}
          />
        </fieldset>
      )}

      {/* ─── Sub-form: evento internacional ─────────────────────────────── */}
      {tipo === 'evento_internacional' && (
        <fieldset className="border border-gray-200 rounded-md p-3 space-y-3">
          <legend className="text-sm font-semibold text-gray-700 px-2">
            Datos del evento internacional
          </legend>
          <Input label="País destino" {...register('subtype_internacional.pais_destino')} />
          <Input
            label="Ciudad destino (opcional)"
            {...register('subtype_internacional.ciudad_destino')}
          />
          <Input
            label="Organización anfitriona (opcional)"
            {...register('subtype_internacional.organizacion_anfitriona')}
          />
          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium text-gray-700">Tipo de evento internacional</span>
            <select
              className="px-3 py-2 border border-gray-300 rounded-md"
              {...register('subtype_internacional.tipo_evento_internacional')}
            >
              <option value="intercambio">Intercambio</option>
              <option value="congreso">Congreso</option>
              <option value="campamento">Campamento</option>
              <option value="competencia">Competencia</option>
              <option value="otro">Otro</option>
            </select>
          </label>
          <Input
            label="Participantes internacionales"
            type="number"
            {...register('subtype_internacional.participantes_internacionales', {
              valueAsNumber: true,
            })}
          />
        </fieldset>
      )}

      <div className="flex gap-3 pt-2">
        <Button type="submit" variant="primary" disabled={isSubmitting}>
          {isSubmitting ? 'Guardando…' : 'Crear actividad'}
        </Button>
      </div>
    </form>
  );
}

// ─── Edit variant (master fields solo, tipo readonly) ─────────────────────

function ActividadFormEdit({
  initialValues,
  onSubmit,
  isSubmitting,
  serverError,
}: ActividadFormEditProps) {
  // biome-ignore lint/suspicious/noExplicitAny: same workaround
  const resolver = zodResolver(ActividadUpdatePayloadSchema) as any;
  const {
    register,
    handleSubmit,
    formState: { errors },
    // biome-ignore lint/suspicious/noExplicitAny: see above
  } = useForm<any>({
    resolver,
    mode: 'onBlur',
    defaultValues: initialValues,
  });

  // biome-ignore lint/suspicious/noExplicitAny: form payload pre-zod
  const handleFormSubmit = (raw: any) => {
    const transformed = {
      ...raw,
      evidencias_urls: splitLines(raw.evidencias_urls_text),
      fotos: splitLines(raw.fotos_text),
    };
    // Quitar campos auxiliares text
    transformed.evidencias_urls_text = undefined;
    transformed.fotos_text = undefined;
    onSubmit(transformed as ActividadUpdatePayload);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      {serverError && (
        <div
          className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700"
          role="alert"
        >
          {serverError}
        </div>
      )}

      <div className="text-sm text-gray-500">
        Tipo: <span className="font-mono">{initialValues.tipo}</span> · No se puede cambiar el tipo.
        Crea nueva actividad si necesitas otro tipo.
      </div>

      <Input
        label="Nombre"
        {...register('nombre')}
        error={errors.nombre?.message as string | undefined}
      />
      <Input
        label="Lugar"
        {...register('lugar')}
        error={errors.lugar?.message as string | undefined}
      />
      <Input
        label="Fecha de la actividad"
        type="date"
        {...register('fecha_actividad')}
        error={errors.fecha_actividad?.message as string | undefined}
      />
      <Input
        label="Participantes"
        type="number"
        {...register('participantes', { valueAsNumber: true })}
        error={errors.participantes?.message as string | undefined}
      />
      <Input
        label="Horas"
        type="number"
        step="0.5"
        {...register('horas', { valueAsNumber: true })}
        error={errors.horas?.message as string | undefined}
      />

      <label className="flex flex-col gap-1">
        <span className="text-sm font-medium text-gray-700">Evidencias (URLs, una por línea)</span>
        <textarea
          className="px-3 py-2 border border-gray-300 rounded-md"
          rows={3}
          {...register('evidencias_urls_text')}
          defaultValue={joinLines(initialValues.evidencias_urls)}
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-sm font-medium text-gray-700">
          Fotos (URLs, máx 5, una por línea)
        </span>
        <textarea
          className="px-3 py-2 border border-gray-300 rounded-md"
          rows={3}
          {...register('fotos_text')}
          defaultValue={joinLines(initialValues.fotos)}
        />
      </label>

      <div className="flex gap-3 pt-2">
        <Button type="submit" variant="primary" disabled={isSubmitting}>
          {isSubmitting ? 'Guardando…' : 'Guardar cambios'}
        </Button>
      </div>
    </form>
  );
}
